<script setup>
import { reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import Api from 'dashboard/api/customers';
import ContactsApi from 'dashboard/api/contacts';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const emit = defineEmits(['saved']);
const { t } = useI18n();
const dialog = ref(null);
const customer = ref(null);
const busy = ref(false);
const error = ref('');
const query = ref('');
const contacts = ref([]);
const mode = ref('new');
const { run, abort } = useAbortableRequest();
const form = reactive({
  name: '',
  email: '',
  phone_number: '',
  customer_type: 'lead',
  expires_on: '',
  contact_id: '',
});

watch(query, async value => {
  abort();
  if (!value.trim()) {
    contacts.value = [];
    return;
  }
  try {
    const response = await run(signal =>
      window.axios.get(`${ContactsApi.url}/search`, {
        params: { q: value },
        signal,
      })
    );
    if (response) contacts.value = response.data.payload;
  } catch (e) {
    error.value = t('CUSTOMERS.ERROR');
  }
});

const open = (value = null) => {
  customer.value = value;
  mode.value = 'new';
  error.value = '';
  query.value = '';
  Object.assign(form, {
    name: value?.name || '',
    email: value?.email || '',
    phone_number: value?.phone_number || '',
    customer_type: value?.customer_type || 'lead',
    expires_on: value?.expires_on || '',
    contact_id: '',
  });
  dialog.value.open();
};

const save = async () => {
  busy.value = true;
  error.value = '';
  try {
    const payload = {
      customer_type: form.customer_type,
      expires_on: form.expires_on || null,
    };
    if (!customer.value && mode.value === 'existing')
      payload.contact_id = form.contact_id;
    else
      Object.assign(payload, {
        name: form.name,
        email: form.email || null,
        phone_number: form.phone_number || null,
      });
    const response = customer.value
      ? await Api.update(customer.value.id, payload)
      : await Api.create(payload);
    emit('saved', response.data);
    dialog.value.close();
  } catch (e) {
    error.value =
      e.response?.data?.error ||
      e.response?.data?.message ||
      t('CUSTOMERS.ERROR');
  } finally {
    busy.value = false;
  }
};
defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t(customer ? 'CUSTOMERS.EDIT' : 'CUSTOMERS.ADD')"
    :confirm-button-label="t('CUSTOMERS.SAVE')"
    :is-loading="busy"
    :disable-confirm-button="
      busy ||
      (!customer && mode === 'existing' ? !form.contact_id : !form.name.trim())
    "
    @confirm="save"
  >
    <div class="flex flex-col gap-4">
      <p v-if="error" role="alert" class="text-n-ruby-11">{{ error }}</p>
      <label v-if="!customer" class="flex flex-col gap-2">
        {{ t('CUSTOMERS.CREATE_MODE') }}
        <select
          v-model="mode"
          class="rounded-lg border border-n-weak bg-n-background p-2"
        >
          <option value="new">{{ t('CUSTOMERS.NEW_CONTACT') }}</option>
          <option value="existing">
            {{ t('CUSTOMERS.EXISTING_CONTACT') }}
          </option>
        </select>
      </label>
      <template v-if="!customer && mode === 'existing'">
        <Input v-model="query" :label="t('CUSTOMERS.FIND_CONTACT')" />
        <label class="flex flex-col gap-2">
          {{ t('CUSTOMERS.SELECT_CONTACT') }}
          <select
            v-model="form.contact_id"
            class="rounded-lg border border-n-weak bg-n-background p-2"
          >
            <option value="">{{ t('CUSTOMERS.SELECT_CONTACT') }}</option>
            <option
              v-for="contact in contacts"
              :key="contact.id"
              :value="contact.id"
            >
              {{ contact.name }} — {{ contact.email || contact.phone_number }}
            </option>
          </select>
        </label>
      </template>
      <template v-else>
        <Input v-model="form.name" :label="t('CUSTOMERS.NAME')" />
        <Input
          v-model="form.email"
          :label="t('CUSTOMERS.EMAIL')"
          type="email"
        />
        <Input
          v-model="form.phone_number"
          :label="t('CUSTOMERS.PHONE')"
          type="tel"
        />
      </template>
      <label class="flex flex-col gap-2">
        {{ t('CUSTOMERS.TYPE') }}
        <select
          v-model="form.customer_type"
          :disabled="!!customer?.first_paid_at"
          class="rounded-lg border border-n-weak bg-n-background p-2"
        >
          <option value="lead">{{ t('CUSTOMERS.TYPES.lead') }}</option>
          <option value="existing_client">
            {{ t('CUSTOMERS.TYPES.existing_client') }}
          </option>
        </select>
      </label>
      <p
        v-if="
          form.customer_type === 'existing_client' && !customer?.first_paid_at
        "
        class="text-sm text-n-slate-11"
      >
        {{ t('CUSTOMERS.PAID_WARNING') }}
      </p>
      <Input
        v-model="form.expires_on"
        :label="t('CUSTOMERS.EXPIRY')"
        type="date"
      />
    </div>
  </Dialog>
</template>
